// The note quotes the live rewrite price rather than a literal, so an env
// change to `NEXT_PUBLIC_CITATION_REWRITE_CREDITS` cannot leave the catalog
// telling users the wrong number.
import { CITATION_REWRITE_CREDITS } from "@repo/payments/client";
import type { WorkflowDefinition } from "../types";

/**
 * W6 — Competitive displacement.
 *
 * One rival, one quarter, a fixed list of grounding searches. `shared` because
 * the target list is the property's: the account team works the same list, and
 * the state it observes — competitors, scored pages, watched publishers, the
 * project — is property state.
 *
 * Two steps are `manual` on purpose. Reading the competitor pages holding the
 * ranks, and reading which third parties carry them, are judgement calls the
 * product cannot observe; what it *can* observe is the work that follows, which
 * is what the fourth step checks.
 */
export const competitiveDisplacementWorkflow: WorkflowDefinition = {
	id: "competitive-displacement",
	wNumber: 6,
	title: "Displace a competitor in AI answers",
	outcome:
		"Take share from one named rival on a list of searches you pick up front.",
	audiences: ["agencies", "inhouse"],
	uses: [
		"Competitors",
		"SEO opportunities",
		"Citation Intelligence",
		"Citation Optimizer",
		"Projects",
	],
	// Needs grounding searches to work backwards from, so BRAND and PRODUCT
	// only: a PERSON's identity prompts emit none.
	isApplicable: (mode) => mode.showGroundingSearches,
	scope: "property",
	concurrency: "shared",
	progress: "explicit",
	articleSlug: "how-to-displace-a-competitor-in-ai-answers",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "pick-competitor",
			title: "Pick one rival and name how they win",
			page: "Competitors",
			route: "/dashboard/competitors",
			criterion: "You've picked the rival and written down how they win.",
			why: "Share of voice is a scoreboard, not a work plan. Aim one rung up the table rather than at the category leader.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Read the competitor breakdown by share of voice",
				"Pick the name one place above and check it is tracked",
				"Write one sentence on how they are winning",
			],
			// Choosing *which* rival, and writing down how they are ahead, is a
			// judgement call. A competitor set the account already had says
			// nothing about whether that choice has been made.
			complete: { kind: "manual" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Competitors tracked",
							value: state.property.competitorCount,
							target: 1,
						}
					: null,
		},
		{
			id: "find-the-queries",
			title: "List the searches where they win",
			page: "SEO opportunities",
			route: "/dashboard/seo",
			criterion: "You've built a target list of searches.",
			why: "These are the web searches AI makes before it answers. Winning them is what moves real answers.",
			docsHref: "/docs/dashboards/ai-visibility-rankings",
			tasks: [
				"List searches where the rival ranks and you do not",
				"Cut searches that belong to a different buyer",
				"Aim for eight to twelve targets, then open their pages",
			],
			// Judgement, not an observable: only "mark done" completes it.
			complete: { kind: "manual" },
			enrichment: "seoGaps",
		},
		{
			id: "trace-sources",
			title: "Find the third parties carrying them",
			page: "Citation Intelligence",
			route: "/citation-intelligence",
			criterion:
				"You've split the work into what you own and what you earn.",
			why: "Reviews, roundups and trade titles carry more weight than a rival's own site. AI leans on sources that look independent.",
			docsHref: "/docs/dashboards/citation-intelligence",
			tasks: [
				"Filter citations to answers that mention the rival",
				"Split it into pages you can write and coverage you must earn",
				"Score the earned list before committing outreach hours",
			],
			complete: { kind: "manual" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Publishers on a pitch list",
							value: state.property.pitchListItemCount,
						}
					: null,
		},
		{
			id: "build-and-pitch",
			title: "Build the pages and the pitch list",
			page: "Citation Optimizer",
			route: "/citation-optimizer",
			criterion:
				"You've scored pages, scored placements, or added publishers to a pitch list.",
			why: "Four to six pages and three to five outlets is a realistic quarter. Start where you are already close.",
			docsHref: "/docs/dashboards/citation-optimizer",
			tasks: [
				"Score your closest pages against their target search",
				"Score the target outlets before spending a month on any of them",
				"Budget outreach separately from content",
			],
			complete: {
				kind: "any",
				of: [
					{ kind: "since", event: "pipelineRunCompleted" },
					{ kind: "since", event: "placementScored" },
					{ kind: "count", key: "pitchListItemCount", gte: 1 },
				],
			},
			spend: {
				kind: "free",
				note: `Rewrites cost ${CITATION_REWRITE_CREDITS} credits each. Pitching and scoring outlets are free.`,
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: `${state.property.pipeline.completed} pages scored`,
									done: state.property.pipeline.completed > 0,
								},
								{
									label: `${state.property.placements.completed} placements scored`,
									done:
										state.property.placements.completed > 0,
								},
								{
									label: `${state.property.pitchListItemCount} publishers on a pitch list`,
									done: state.property.pitchListItemCount > 0,
								},
							],
						}
					: null,
		},
		{
			id: "measure-the-shift",
			title: "Track the shift as a project",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've created a project since you started.",
			why: "Record where you stand before anything ships. Report the list you named on day one, not the overall trend.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create the project before the first page ships",
				"Add the prompts and goals covering your target searches",
				"Review monthly, not weekly",
			],
			// The baseline has to be recorded before the first page ships, which
			// means a project created for this quarter's target list — not an
			// unrelated project the account was already running.
			complete: { kind: "since", event: "projectCreated" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active projects",
							value: state.property.activeProjectCount,
							target: 1,
						}
					: null,
		},
	],
};
